/** Motivos / ocorrências oficiais do Bolsa Família (crianças e mulheres). */
export const motivosOcorrencia = [
  {
    id: 22,
    ds: 'Condições de saúde que impedem a ida à UBS',
  },
  {
    id: 23,
    ds: 'Fatos que impedem o deslocamento/acesso à UBS (enchente, falta de transporte, violência no território, etc.).',
  },
  {
    id: 24,
    ds: 'Horário de atendimento na UBS inviável para o(a) responsável/beneficiário(a).',
  },
  {
    id: 25,
    ds: 'Responsável/Beneficiário(a) não cumpriu com as condicionalidades por questões sociais, culturais, étnicas ou religiosas.',
  },
  {
    id: 27,
    ds: 'Houve recusa em realizar o acompanhamento das condicionalidades dentro da rotina de Atenção Básica de Saúde.',
  },
  {
    id: 28,
    ds: 'Indícios de situação de risco social tal como negligência, abuso sexual, violência intrafamiliar ou outras.',
  },
  {
    id: 29,
    ds: 'Responsável/Beneficiário(a) afirma que a não é mais do programa.',
  },
  {
    id: 30,
    ds: 'Falta de oferta de serviço de pré-natal.',
  },
]

/**
 * Retorna a vigência (1 = jan-jun, 2 = jul-dez).
 *
 * @param {number} mes
 * @returns {number}
 */
export function getVigenciaPorMes(mes) {
  return mes <= 6 ? 1 : 2
}

/**
 * Monta o objeto de ocorrência no formato da API.
 *
 * @param {{ id: number, ds: string }} motivo
 * @param {string} profissional
 * @param {Date} [dataRef]
 * @returns {Object}
 */
export function montarOcorrencia(motivo, profissional, dataRef = new Date()) {
  const ano = dataRef.getFullYear()
  const mes = dataRef.getMonth() + 1
  const dia = String(dataRef.getDate()).padStart(2, '0')
  const mesStr = String(mes).padStart(2, '0')

  return {
    id: motivo.id,
    ds: motivo.ds,
    dt: `${ano}-${mesStr}-${dia}`,
    mes,
    ano,
    vig: getVigenciaPorMes(mes),
    prof: profissional || '',
  }
}

/**
 * Faz parse de JSON string, inclusive se vier duplamente serializada.
 *
 * @param {string} texto
 * @returns {*}
 */
function parseJsonFlex(texto) {
  let valor = texto.trim()
  if (!valor) return null

  for (let i = 0; i < 2; i++) {
    try {
      const parsed = JSON.parse(valor)
      if (typeof parsed === 'string') {
        valor = parsed
        continue
      }
      return parsed
    } catch (err) {
      return null
    }
  }

  return null
}

/**
 * Normaliza um item de ocorrência (objeto ou JSON string).
 *
 * @param {Object|string|null} ocorrencia
 * @returns {Object|null}
 */
function normalizarItemOcorrencia(ocorrencia) {
  if (!ocorrencia) return null

  if (typeof ocorrencia === 'string') {
    const parsed = parseJsonFlex(ocorrencia)
    if (Array.isArray(parsed)) {
      return null
    }
    if (parsed && typeof parsed === 'object' && (parsed.id || parsed.ds)) {
      return parsed
    }
    return null
  }

  if (typeof ocorrencia === 'object' && (ocorrencia.id || ocorrencia.ds)) {
    return ocorrencia
  }

  return null
}

/**
 * Normaliza ocorrências da API para sempre retornar um array.
 * Aceita: array, objeto único ou JSON string
 * (ex.: "[{\"id\":22,\"ds\":\"...\"}]").
 *
 * @param {Array|Object|string|null} ocorrencia
 * @returns {Array}
 */
export function normalizarListaOcorrencias(ocorrencia) {
  if (!ocorrencia) return []

  if (typeof ocorrencia === 'string') {
    const parsed = parseJsonFlex(ocorrencia)
    if (parsed == null) return []
    return normalizarListaOcorrencias(parsed)
  }

  if (Array.isArray(ocorrencia)) {
    return ocorrencia.map(normalizarItemOcorrencia).filter(Boolean)
  }

  const item = normalizarItemOcorrencia(ocorrencia)
  return item ? [item] : []
}

/**
 * Retorna a primeira ocorrência (compatível com o modal de edição).
 *
 * @param {Array|Object|string|null} ocorrencia
 * @returns {Object|null}
 */
export function normalizarOcorrencia(ocorrencia) {
  const lista = normalizarListaOcorrencias(ocorrencia)
  return lista[0] || null
}

/**
 * Normaliza a lista de cadastros, tipando o campo ocorrencia como array.
 *
 * @param {Array} cadastros
 * @returns {Array}
 */
export function normalizarCadastros(cadastros) {
  if (!Array.isArray(cadastros)) return []

  return cadastros.map((item) => ({
    ...item,
    ocorrencia: normalizarListaOcorrencias(item.ocorrencia),
  }))
}

/**
 * Texto de exibição da ocorrência na tabela.
 *
 * @param {Object|string|null} ocorrencia
 * @returns {string}
 */
export function formatarOcorrenciaExibicao(ocorrencia) {
  const dados = normalizarItemOcorrencia(ocorrencia) || normalizarOcorrencia(ocorrencia)
  if (!dados) return ''

  const codigo = dados.id != null ? `${dados.id} - ` : ''
  return `${codigo}${dados.ds || ''}`.trim()
}

/**
 * Indica se o item já possui ocorrência.
 *
 * @param {Object} item
 * @returns {boolean}
 */
export function temOcorrencia(item) {
  return normalizarListaOcorrencias(item?.ocorrencia).length > 0
}

/**
 * Monta o payload de save/edição no formato da API.
 *
 * @param {string|number} nis
 * @param {Object|Array} ocorrencia
 * @returns {{ nis: string, ocorrencia: Array }}
 */
export function montarPayloadOcorrencia(nis, ocorrencia) {
  const lista = Array.isArray(ocorrencia)
    ? ocorrencia
    : ocorrencia
      ? [ocorrencia]
      : []

  return {
    nis: String(nis || ''),
    ocorrencia: lista,
  }
}
